import format from 'date-fns/format';
import { FC, Fragment, useState } from 'react';

import { AppointmentTag, AppointmentTagData } from '@eluve/blocks';
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Icon,
  Separator,
  SimpleTooltip,
  Stack,
} from '@eluve/components';

export type FileTagAddedByUser = {
  email: string;
  firstName: string;
  lastName: string;
  displayName: string | null;
};

export type FileTag = AppointmentTagData & {
  metadata: unknown;
  addedById: string | null;
  addedByUser: FileTagAddedByUser | null;
};

export interface FileTagsDialogProps {
  tags: FileTag[];
}

export const FileTagsDialog: FC<FileTagsDialogProps> = ({ tags }) => {
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const onOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setSelectedId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <SimpleTooltip label="View Tags">
        <DialogTrigger asChild>
          <Button variant="outline" disabled={tags.length === 0}>
            <Icon name="Tag" size="auto" />
            {`${tags.length}`}
          </Button>
        </DialogTrigger>
      </SimpleTooltip>
      <DialogContent size="lg" className="max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>File Tags</DialogTitle>
        </DialogHeader>
        <Separator className="mt-2 mb-0" />
        <Stack align="stretch" gap={3}>
          {tags.map((tag, index) => {
            const hasMetadata =
              tag.metadata != null &&
              typeof tag.metadata === 'object' &&
              Object.keys(tag.metadata as object).length > 0;
            const isSelected = selectedId === tag.id;
            const formattedAddedAt = format(
              new Date(tag.createdAt),
              'MMM d, yyyy - h:mm a',
            );
            const addedByName = tag.addedByUser
              ? (tag.addedByUser.displayName ??
                `${tag.addedByUser.firstName} ${tag.addedByUser.lastName}`.trim())
              : null;
            return (
              <Fragment key={tag.id}>
                <Collapsible
                  className="group"
                  onOpenChange={(open) => setSelectedId(open ? tag.id : null)}
                  open={isSelected}
                >
                  <Stack justify="between" gap={2} direction="horizontal">
                    <AppointmentTag tag={tag} />
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <Icon
                          name={isSelected ? 'ChevronUp' : 'ChevronDown'}
                          size="auto"
                        />
                      </Button>
                    </CollapsibleTrigger>
                  </Stack>
                  <CollapsibleContent>
                    <Stack align="stretch" gap={1} className="mt-3 text-xs">
                      {tag.addedByUser ? (
                        <p>
                          <span className="opacity-60">Added by: </span>
                          {addedByName} ({tag.addedByUser.email})
                        </p>
                      ) : tag.addedById ? (
                        <p>
                          <span className="opacity-60">Added by: </span>
                          unknown user
                        </p>
                      ) : (
                        <p>
                          <span className="opacity-60">Applied by: </span>
                          system
                        </p>
                      )}
                      <p>
                        <span className="opacity-60">At (browser local): </span>
                        {formattedAddedAt}
                      </p>
                    </Stack>
                    {hasMetadata && (
                      <pre className="mt-3 overflow-x-auto rounded-md border bg-bg-secondary p-3 text-xs">
                        {JSON.stringify(tag.metadata, null, 2)}
                      </pre>
                    )}
                  </CollapsibleContent>
                </Collapsible>
                {index < tags.length - 1 && <Separator />}
              </Fragment>
            );
          })}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
